// MIT License - Copyright (c) 2026 Doug McNall
//
// Cart context with server-side persistence for logged-in users.
//
// Strategy:
//   Guest:       localStorage only (key: ddhotsauce_cart)
//   Logged in:   server is source of truth; mutations debounce-sync via POST /cart
//
// On login:  server cart is fetched; local items merged in (local wins on conflict)
// On logout: final cart pushed to server, localStorage cleared
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import apiConfig from '../config/api-config';

const CartContext = createContext();
const LS_KEY = 'ddhotsauce_cart';
const SYNC_DEBOUNCE_MS = 800;
const API = apiConfig.apiUrl;

const csrfToken = () => {
  const m = document.cookie.match(/(?:^|;\s*)ddhs_shop_csrf=([^;]+)/);
  return m ? m[1] : '';
};

const loadLocal = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; }
  catch { return []; }
};

const saveLocal = (items) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(items)); }
  catch { /* ignore quota errors */ }
};

// Merge local items into server items — local wins on quantity conflict.
const mergeItems = (serverItems, localItems) => {
  const merged = [...serverItems];
  for (const local of localItems) {
    const key = local.id ?? local.product_id;
    const idx = merged.findIndex(s => (s.id ?? s.product_id) === key);
    if (idx === -1) merged.push(local);
    else merged[idx] = { ...merged[idx], quantity: merged[idx].quantity + local.quantity };
  }
  return merged;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [cartItems, setCartItems] = useState(loadLocal);
  const [syncing, setSyncing] = useState(false);
  const debounceRef = useRef(null);
  // Track the last user we loaded for, to avoid re-fetching on re-renders.
  const loadedForRef = useRef(null);

  // -- Server sync helpers --------------------------------------------------

  const pushToServer = useCallback(async (items) => {
    try {
      await fetch(`${API}/cart`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ items }),
      });
    } catch { /* best-effort */ }
  }, []);

  const fetchFromServer = useCallback(async () => {
    const res = await fetch(`${API}/cart`, { credentials: 'include' });
    if (!res.ok) throw new Error('cart fetch failed');
    const data = await res.json();
    return data.items || [];
  }, []);

  // -- Auth change: load server cart on login, push+clear on logout ---------

  useEffect(() => {
    if (isAuthenticated && user?.id && loadedForRef.current !== user.id) {
      loadedForRef.current = user.id;
      setSyncing(true);
      fetchFromServer()
        .then((serverItems) => {
          const local = loadLocal();
          const merged = local.length ? mergeItems(serverItems, local) : serverItems;
          setCartItems(merged);
          saveLocal(merged);
          if (local.length) pushToServer(merged);  // persist merge back
        })
        .catch(() => { /* stay with local on network error */ })
        .finally(() => setSyncing(false));
    }
    if (!isAuthenticated) {
      loadedForRef.current = null;
    }
  }, [isAuthenticated, user?.id, fetchFromServer, pushToServer]);

  // -- Debounced server sync on every cart change ---------------------------

  // Use a ref so the timeout closure always sees the latest auth state.
  const isAuthRef = useRef(isAuthenticated);
  useEffect(() => { isAuthRef.current = isAuthenticated; }, [isAuthenticated]);

  const scheduleSyncToServer = useCallback((items) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (isAuthRef.current) pushToServer(items);
    }, SYNC_DEBOUNCE_MS);
  }, [pushToServer]);

  // -- Always persist to localStorage ---------------------------------------

  useEffect(() => {
    saveLocal(cartItems);
  }, [cartItems]);

  // -- Mutation helpers (unchanged API surface) -----------------------------

  const _set = (updater) => {
    setCartItems(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      scheduleSyncToServer(next);
      return next;
    });
  };

  const addToCart = (product, quantity) => {
    _set(prev => {
      const key = product.id ?? product.product_id;
      const existing = prev.find(i => (i.id ?? i.product_id) === key);
      return existing
        ? prev.map(i => (i.id ?? i.product_id) === key ? { ...i, quantity: i.quantity + quantity } : i)
        : [...prev, { ...product, quantity }];
    });
  };

  const removeFromCart = (id) => {
    _set(prev => prev.filter(i => (i.id ?? i.product_id) !== id));
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    _set(prev => prev.map(i => (i.id ?? i.product_id) === id ? { ...i, quantity: newQuantity } : i));
  };

  const clearCart = () => _set([]);

  const cartItemCount = cartItems.reduce((sum, i) => sum + i.quantity, 0);
  const cartTotal = cartItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);

  return (
    <CartContext.Provider value={{
      cartItems, syncing,
      addToCart, removeFromCart, updateQuantity, clearCart,
      cartItemCount, cartTotal,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
